import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";
import type { TrapPacketDto } from "@gryzonie/shared";
import { useAuthStore } from "../auth/auth.store";

interface Options {
  enabled?: boolean;
  onPacket: (packet: TrapPacketDto) => void;
  onConnectionChange?: (status: "connecting" | "connected" | "disconnected") => void;
}

/**
 * Subscribes to the /ws/traps namespace. Reconnects automatically when the
 * access token changes (e.g. after silent refresh).
 */
export function useTrapSocket({ enabled = true, onPacket, onConnectionChange }: Options) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const onPacketRef = useRef(onPacket);
  const onStatusRef = useRef(onConnectionChange);

  useEffect(() => {
    onPacketRef.current = onPacket;
    onStatusRef.current = onConnectionChange;
  });

  useEffect(() => {
    if (!enabled || !accessToken) return;
    onStatusRef.current?.("connecting");
    const url = `${window.location.origin}`;
    const socket: Socket = io(`${url}/ws/traps`, {
      transports: ["websocket"],
      auth: { token: accessToken },
      withCredentials: true,
    });

    socket.on("connect", () => onStatusRef.current?.("connected"));
    socket.on("disconnect", () => onStatusRef.current?.("disconnected"));
    socket.on("packet:new", (packet: TrapPacketDto) => onPacketRef.current(packet));
    socket.on("connect_error", () => onStatusRef.current?.("disconnected"));

    return () => {
      socket.disconnect();
    };
  }, [accessToken, enabled]);
}
